import { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";
import { IGetPixByOrderService } from "@modules/orders/models/IGetPixByOrderService";
import { PixPayload } from "@pages/User/MyOrders/models/IPixPayment";
import { PixModalPaymentStatus } from "../types";

interface UsePixPaymentModalOptions {
  salesOrderNumber: string | null;
  bp?: string | null;
  enabled?: boolean;
  pollingInterval?: number;
}

interface UsePixPaymentModalReturn {
  open: boolean;
  openModal: () => void;
  closeModal: () => void;
  loading: boolean;
  refreshing: boolean;
  payload: PixPayload | null;
  paymentStatus: PixModalPaymentStatus;
  error: string | null;
  refreshQrCode: () => Promise<void>;
}

export function usePixPaymentModal({
  salesOrderNumber,
  bp,
  enabled = true,
  pollingInterval = 60000,
}: UsePixPaymentModalOptions): UsePixPaymentModalReturn {
  const iocContext = useIoCContext();
  const { enqueueSnackbar } = useSnackbar();

  const getPixByOrderServiceRef = useRef<IGetPixByOrderService>(
    iocContext.serviceContainer.get<IGetPixByOrderService>(
      Types.Orders.IGetPixByOrderService
    )
  );

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [payload, setPayload] = useState<PixPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  // Flag para evitar que o polling sobrescreva o resultado do refresh
  const isRefreshingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const fetchPix = useCallback(
    async (isRefresh = false) => {
      if (!salesOrderNumber) return;
      // Não executa se um refresh manual está em andamento
      if (isRefreshingRef.current) return;

      try {
        if (!isRefresh) {
          // Só mostra loading na carga inicial — o polling atualiza silenciosamente
          setLoading(true);
        }
        const result = await getPixByOrderServiceRef.current.execute(
          salesOrderNumber
        );
        const mainPayload = result.find((p) => p.main) || result[0] || null;
        setPayload(mainPayload);
        setError(null);
      } catch (err: any) {
        // Polling silencioso: não exibe erro para não perturbar o usuário
        if (!isRefresh) {
          setError(err.message ?? "Erro ao carregar dados do Pix");
        }
      } finally {
        setLoading(false);
      }
    },
    [salesOrderNumber]
  );

  const paymentStatus: PixModalPaymentStatus =
    payload?.paymentStatus === "PAID" || payload?.status === "pago"
      ? "confirmed"
      : "pending";

  const openModal = useCallback(() => {
    setOpen(true);
    if (enabled && salesOrderNumber) {
      fetchPix(false);
    }
  }, [enabled, salesOrderNumber, fetchPix]);

  const closeModal = useCallback(() => {
    setOpen(false);
    stopPolling();
  }, [stopPolling]);

  const refreshQrCode = useCallback(async () => {
    if (!salesOrderNumber || !bp) return;

    // O intervalo de polling segue ativo, mas isRefreshingRef bloqueia o
    // fetchPix durante o refresh, evitando que o QR Code novo seja
    // sobrescrito pela resposta em cache do GET /pix/qrcode. Ao concluir,
    // o polling volta a executar naturalmente no próximo tick.
    isRefreshingRef.current = true;

    try {
      setRefreshing(true);
      const emv = await getPixByOrderServiceRef.current.refresh(
        salesOrderNumber,
        bp
      );
      setPayload((prev) => (prev ? { ...prev, copiaECola: emv } : prev));
      setError(null);
    } catch (err: any) {
      enqueueSnackbar(
        err?.message ??
          "Não foi possível atualizar o QR Code. Tente novamente.",
        { variant: "error" }
      );
    } finally {
      setRefreshing(false);
      isRefreshingRef.current = false;
    }
  }, [salesOrderNumber, bp, enqueueSnackbar]);

  // Polling enquanto modal está aberto e pagamento pendente
  useEffect(() => {
    if (open && paymentStatus === "pending" && salesOrderNumber) {
      pollingRef.current = setInterval(() => {
        fetchPix(true);
      }, pollingInterval);
    }

    return () => {
      stopPolling();
    };
  }, [
    open,
    paymentStatus,
    salesOrderNumber,
    pollingInterval,
    fetchPix,
    stopPolling,
  ]);

  return {
    open,
    openModal,
    closeModal,
    loading,
    refreshing,
    payload,
    paymentStatus,
    error,
    refreshQrCode,
  };
}
